import { msg, t } from "@lingui/core/macro";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router";
import { patientApi } from "./ApiClient";
import { generatePatches, generateQueryString } from "./Helpers";
import type {
  IDeclinedCategory,
  IDeclinedPatient,
  IDeletedPatient,
  IDischargedPatient,
  IExistingPatient,
  IGivenAndFamilyNameInput,
  IPatientStatus,
  IPersonalIdentityNumberType,
  IRegisteredPatient,
  IRelative,
} from "@medoma/models/patients";
import type { listPatientSchema } from "@medoma/models/patients";
import {
  patientStatusSchema,
  patientStatusDictionary,
  patientNameType,
  existingPatientSchema,
  listExistingPatientSchema,
  type IAdmittedPatient,
  getPatientFullName,
  getPatientFamilyName,
  getPatientGivenName,
  careHubsSchema,
  careEpisodeSchema,
  economicUnitsSchema,
  patientTypesSchema,
  patientTypeSchema,
  personalIdentityNumberTypeSchema,
  givenAndFamilyNameSchema,
  relativeSchema,
  areasSchema,
  teamsSchema,
  wardsSchema,
} from "@medoma/models/patients";
import {
  addressWithCoordinates,
  type IAddressWithCoordinates,
} from "@medoma/models/addresses";
import { siteSchema, type SiteId } from "@medoma/models/sites";
import { i18n } from "@lingui/core";
import { z } from "zod";
import {
  rawPhoneNumberSchema,
  type RawPhoneNumber,
} from "@medoma/models/phoneNumbers";
import { internalPostalCodeSchema } from "@medoma/models/postalCode";
import { calendarDateSchema } from "@medoma/models/date-and-time";

export type IListPatient = z.infer<typeof listPatientSchema>;

const existingPatientsSchema = z.array(listExistingPatientSchema);

export const patientKeys = {
  all: ["patients"] as const,
  lists: () => [...patientKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...patientKeys.lists(), { filters }] as const,
  listWithVariant: (filters: Record<string, unknown>) =>
    [...patientKeys.lists(), "withHasHomeVisit", filters] as const,
  detail: (id: string) => [...patientKeys.all, id, "details"] as const,
};

export const fetchPatients = async ({
  patientIds,
  statuses,
}: {
  patientIds?: string[];
  statuses?: IPatientStatus[];
}) => {
  const queryString =
    patientIds && patientIds.length > 100
      ? // MED-6269: We ran into ~8000 character limit in an invoicing scenario. Avoid this by asking for all patients instead.
        // This is OK, because the patientId filter is only used to reduce the load, not for functionality.
        generateQueryString({ status: statuses })
      : // This is normal usage
        generateQueryString({ id: patientIds, status: statuses });
  const parsedPatients = await patientApi
    .get(`/ambulating/patients${queryString}`)
    .json(existingPatientsSchema);
  return parsedPatients;
};

export const createDeletedPatient = (patientId: string) => ({
  id: patientId,
  status: patientStatusSchema.enum.deleted,
});

// Every patient id resolves to a patient: one that is missing from the list was
// deleted, and the stub stands in for it. A patient that is absent altogether is
// modelled by having no patientId to resolve, so callers that allow that decide
// what to do with it rather than asking here.
export const resolvePatient = ({
  patientId,
  patients,
}: {
  patientId: string;
  patients: ReadonlyArray<IListPatient>;
}): IListPatient =>
  patients.find((patient) => patient.id === patientId) ??
  createDeletedPatient(patientId);

export async function fetchPatient(id: string) {
  const parsedPatient = await patientApi
    .get(`/ambulating/patients/${id}`)
    .json(existingPatientSchema);
  return parsedPatient;
}

export const usePatient = (patientId: string) => {
  return useQuery({
    queryKey: patientKeys.detail(patientId),
    queryFn: () => fetchPatient(patientId),
  });
};

export const fetchCareHub = async (siteId: SiteId) => {
  const careHubs = await patientApi
    .get(`/ambulating/${siteId}/carehubs`)
    .json(careHubsSchema);

  if (!careHubs || !careHubs[0]) {
    throw new Error("No care hubs were found");
  }

  return careHubs[0];
};

export const useCareHub = (siteId: SiteId) => {
  return useQuery({
    queryKey: [...careHubsKeys.all, siteId],
    queryFn: () => fetchCareHub(siteId),
  });
};

const careHubsKeys = {
  all: ["careHubs"] as const,
};

export function useParentRoutesPatient() {
  return useOutletContext<IExistingPatient>();
}

export const updateAddress = async (
  patientId: string,
  address: IAddressWithCoordinates,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ address }),
  });
};

export const updateDiagnosisCode = async (
  patientId: string,
  diagnosisCode: string,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ diagnosisCode }),
  });
};

export const updatePhoneNumber = async (
  patientId: string,
  phoneNumber: RawPhoneNumber,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ phoneNumber }),
  });
};

export const updateName = async (
  patientId: string,
  givenAndFamilyName: IGivenAndFamilyNameInput,
) => {
  const name = {
    ...givenAndFamilyName,
    type: patientNameType.enum.GivenAndFamily,
  };
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ name }),
  });
};

export const updateInformation = async (
  patientId: string,
  information: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ information }),
  });
};

export const addRelative = async (patientId: string, relative: IRelative) => {
  await patientApi.post(`/ambulating/patients/${patientId}/relatives`, {
    json: { ...relative },
  });
};

export const updateRelative = async (
  patientId: string,
  relative: IRelative,
) => {
  await patientApi.patch(
    `/ambulating/patients/${patientId}/relatives/${relative.id}`,
    { json: relative },
  );
};

export const removeRelative = async (patientId: string, relativeId: string) => {
  await patientApi.delete(
    `/ambulating/patients/${patientId}/relatives/${relativeId}`,
  );
};

export const updateHealthcareJourneyNumber = async (
  patientId: string,
  healthcareJourneyNumber: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ healthcareJourneyNumber }),
  });
};

export const updateSafetyAlarmText = async (
  patientId: string,
  safetyAlarmText: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ safetyAlarmText }),
  });
};

export const updatePets = async (
  patientId: string,
  pets: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ pet: pets }),
  });
};

export const updateFlags = async (
  patientId: string,
  flags: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ flags: flags }),
  });
};

export const updateCareSummary = async (
  patientId: string,
  careSummary: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ careSummary }),
  });
};

export const updateHomeCare = async (
  patientId: string,
  homeCare: boolean | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ homeCare: Boolean(homeCare) }), // For checkboxes, this is always a boolean
  });
};

export const updateLifeCare = async (
  patientId: string,
  lifeCare: boolean | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ lifeCare: Boolean(lifeCare) }), // For checkboxes, this is always a boolean
  });
};

export const updateExternalCareEpisodeId = async (
  patientId: string,
  externalCareEpisodeId: string,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ externalCareEpisodeId }),
  });
};

export const updatePlannedAdmissionDateTime = async (
  patientId: string,
  plannedAdmissionDateTime: Date | null,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({
      plannedAdmissionDateTime: calendarDateSchema
        .nullable()
        .encode(plannedAdmissionDateTime),
    }),
  });
};

export const updatePlannedDischargeDateTime = async (
  patientId: string,
  plannedDischargeDateTime: Date | null,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({
      plannedDischargeDateTime: calendarDateSchema
        .nullable()
        .encode(plannedDischargeDateTime),
    }),
  });
};

export const updatePrimaryResponsible = async (
  patientId: string,
  primaryResponsible: number | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ primaryResponsible }),
  });
};

export const updateReferringUnit = async (
  patientId: string,
  referringUnit: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ referringUnit }),
  });
};

export const updateArea = async ({
  patientId,
  area,
}: {
  patientId: string;
  area: number | null;
}) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ area }),
  });
};

export const updateTeam = async ({
  patientId,
  team,
}: {
  patientId: string;
  team: number | null;
}) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ team }),
  });
};

export const updateEconomicUnit = async ({
  patientId,
  economicUnit,
}: {
  patientId: string;
  economicUnit: number;
}) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ economicUnit }),
  });
};

export const updateWard = async (patientId: string, ward: number) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ ward }),
  });
};

export const updatePlaceOfAssessment = async (
  patientId: string,
  placeOfAssessment: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ placeOfAssessment }),
  });
};

export const updatePaymentExempt = async (
  patientId: string,
  paymentExempt: boolean | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ paymentExempt }),
  });
};

export const updateLanguage = async (
  patientId: string,
  language: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ language }),
  });
};

export const updateReasonForAdmission = async (
  patientId: string,
  reasonForAdmission: string | null | undefined,
) => {
  await patientApi.patch(`/ambulating/patients/${patientId}`, {
    json: generatePatches({ reasonForAdmission }),
  });
};

export const getPatientNameWithStatus = (
  patient:
    | Pick<IDeletedPatient, "status">
    | Pick<IRegisteredPatient, "name" | "status">
    | Pick<IDischargedPatient, "name" | "status">
    | Pick<IDeclinedPatient, "name" | "status">
    | Pick<IAdmittedPatient, "name" | "status" | "pausedAt">,
  nameFormat: "family" | "given" | "full",
): string => {
  const formatName =
    nameFormat === "family"
      ? getPatientFamilyName
      : nameFormat === "given"
        ? getPatientGivenName
        : getPatientFullName;
  if (patient.status === patientStatusSchema.enum.admitted) {
    if (patient.pausedAt) {
      return `${formatName(patient.name)} (${t`pausad`})`;
    }
    return formatName(patient.name);
  }

  const localizedPatientStatus = i18n
    ._(patientStatusDictionary[patient.status].singular)
    .toLocaleLowerCase();

  if (patient.status === patientStatusSchema.enum.deleted) {
    return t`Okänd (${localizedPatientStatus})`;
  }

  return `${formatName(patient.name)} (${i18n
    ._(patientStatusDictionary[patient.status].singular)
    .toLocaleLowerCase()})`;
};

const fetchAllSites = async () => {
  // Using "patient API" is a bit of a workaround. See https://medoma.slack.com/archives/C0A24R4FLJV/p1769163525178789 for context
  return await patientApi.get("/sites").json(z.array(siteSchema));
};

export const sitesQueryOptions = queryOptions({
  queryKey: ["sites"],
  queryFn: fetchAllSites,
  staleTime: 1000 * 60 * 10, // 10 minutes
});

/*****************************************************************************************************
 * CONDITIONAL PATIENT INFORMATION FIELDS
 *****************************************************************************************************/

export const conditionalPatientInformationFieldNames = [
  "DiagnosisCode",
  "AdditionalAdressInformation",
  "KeyToPatient",
  "CloseContact",
  "HomeCare",
  "Pet",
  "PersWristband",
  "MedicalTransportNumber",
  "LifeCare",
  "GeneralInformation",
  "ExternalCareEpisodeId",
  "Flags",
  "PrimaryResponsible",
  "ReferringUnit",
  "PlaceOfAssessment",
  "PlannedAdmissionDateTime",
  "PlannedDischargeDateTime",
  "PostDischargeProcessed",
  "PaymentExempt",
  "CareSummary",
  "Language",
  "ReasonForAdmission",
] as const;

export const conditionalPatientInformationFieldNamesDictionary = {
  DiagnosisCode: msg`Diagnoskod`,
  AdditionalAdressInformation: msg`Övrig adressinformation`,
  KeyToPatient: msg`Nyckel`,
  CloseContact: msg`Anhöriga`,
  HomeCare: msg`Hemtjänst`,
  Pet: msg`Husdjur`,
  PersWristband: msg`Sociala insatser`,
  MedicalTransportNumber: msg`Sjukresenummer`,
  LifeCare: msg`LifeCare`,
  GeneralInformation: msg`Allmän information`,
  ExternalCareEpisodeId: msg`Externt vårdperiodnummer`,
  Flags: msg`Flaggningar`,
  PrimaryResponsible: msg`Patientansvarig`,
  ReferringUnit: msg`Remitterande enhet`,
  PlaceOfAssessment: msg`Plats för bedömning`,
  PlannedAdmissionDateTime: msg`Planerad inskrivning`,
  PlannedDischargeDateTime: msg`Planerad utskrivning`,
  PostDischargeProcessed: msg`Efterhanterad`,
  PaymentExempt: msg`Betalningsbefriad`,
  CareSummary: msg`Vårdsammanfattning`,
  Language: msg`Språk`,
  ReasonForAdmission: msg`Inskrivningsorsak`,
};

const conditionalPatientInformationFieldNamesSchema = z.enum(
  conditionalPatientInformationFieldNames,
);

const availablePatientInformationFieldSchema = z.object({
  fieldName: conditionalPatientInformationFieldNamesSchema,
  required: z.boolean(),
});

export const availablePatientInformationFieldsSchema = z.array(
  availablePatientInformationFieldSchema,
);

export type IAvailablePatientInformationFields = z.infer<
  typeof availablePatientInformationFieldsSchema
>;

const fetchPatientInformationFields = async () => {
  const parsedData = await patientApi
    .get("/patients/information-fields")
    .json(availablePatientInformationFieldsSchema);
  return parsedData;
};

export const informationFieldConfigValueSchema = z.enum([
  "hidden",
  "optional",
  "required",
]);

export type IInformationFieldConfigValue = z.infer<
  typeof informationFieldConfigValueSchema
>;

const _informationFieldsConfigSchema = z.record(
  conditionalPatientInformationFieldNamesSchema,
  informationFieldConfigValueSchema,
);
export type IInformationFieldsConfig = z.infer<
  typeof _informationFieldsConfigSchema
>;

const createInformationFieldsConfig = (
  availableFields: IAvailablePatientInformationFields,
) => {
  const config = {} as IInformationFieldsConfig;

  availableFields.forEach((item) => {
    config[item.fieldName] = item.required
      ? informationFieldConfigValueSchema.enum.required
      : informationFieldConfigValueSchema.enum.optional;
  });

  conditionalPatientInformationFieldNames.forEach((field) => {
    if (!config[field]) {
      config[field] = informationFieldConfigValueSchema.enum.hidden;
    }
  });

  return config;
};

// Fields that are only meaningful for a subset of patient statuses live here next to the
// label and config so the per-field rule has one home. A field without an entry is visible
// whenever its config value isn't `hidden`.
const conditionalPatientInformationFieldStatusGate: Partial<
  Record<
    (typeof conditionalPatientInformationFieldNames)[number],
    (status: IPatientStatus) => boolean
  >
> = {
  PlannedAdmissionDateTime: (status) =>
    status === patientStatusSchema.enum.prospect,
  PlannedDischargeDateTime: (status) =>
    status !== patientStatusSchema.enum.prospect,
  PlaceOfAssessment: (status) => status === patientStatusSchema.enum.prospect,
  PostDischargeProcessed: (status) =>
    status === patientStatusSchema.enum.discharged,
};

export const isPatientInformationFieldVisible = ({
  fieldName,
  configValue,
  patientStatus,
}: {
  fieldName: (typeof conditionalPatientInformationFieldNames)[number];
  configValue: IInformationFieldConfigValue;
  patientStatus: IPatientStatus;
}) => {
  if (configValue === informationFieldConfigValueSchema.enum.hidden) {
    return false;
  }
  const gate = conditionalPatientInformationFieldStatusGate[fieldName];
  return gate ? gate(patientStatus) : true;
};

const patientInformationFieldsKeys = {
  all: ["patientInformationFields"] as const,
};

export const usePatientInformationFields = () => {
  return useQuery({
    queryKey: [patientInformationFieldsKeys.all],
    queryFn: async () => {
      const availableFields = await fetchPatientInformationFields();
      const informationFields = createInformationFieldsConfig(availableFields);
      return informationFields;
    },
  });
};

export const usePatients = ({
  statuses,
  sort = "name-asc",
}: {
  statuses: IPatientStatus[];
  sort?: "name-asc";
}) => {
  return useQuery({
    queryKey: patientKeys.list({ statuses, sort }),
    queryFn: async () =>
      (await fetchPatients({ statuses })).sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return getPatientFullName(a.name).localeCompare(
              getPatientFullName(b.name),
            );
          default:
            return 0;
        }
      }),
  });
};

/*****************************************************************************************************
 * PATIENT REGISTRATION
 *****************************************************************************************************/

const fetchPatientTypes = async () => {
  return await patientApi
    .get(`/ambulating/patients/types`)
    .json(patientTypesSchema);
};

const patientTypesKeys = {
  all: ["types"] as const,
};

export const usePatientTypes = () => {
  return useQuery({
    queryKey: patientTypesKeys.all,
    queryFn: () => fetchPatientTypes(),
    staleTime: 10 * 60 * 1000,
  });
};

const fetchEconomicUnits = async (siteId: SiteId) => {
  return await patientApi
    .get(`/ambulating/${siteId}/economic-units`)
    .json(economicUnitsSchema);
};

const economicUnitsKeys = {
  all: ["economicUnits"] as const,
  bySite: (siteId: SiteId) => [...economicUnitsKeys.all, siteId] as const,
};

export const useEconomicUnits = (
  siteId: SiteId,
  { enabled }: { enabled: boolean },
) => {
  return useQuery({
    queryKey: economicUnitsKeys.bySite(siteId),
    queryFn: () => fetchEconomicUnits(siteId),
    staleTime: 1000 * 60 * 10,
    enabled,
  });
};

const fetchAreas = async (siteId: SiteId) => {
  return await patientApi.get(`/ambulating/${siteId}/areas`).json(areasSchema);
};

const areasKeys = {
  all: ["areas"] as const,
  bySite: (siteId: SiteId) => [...areasKeys.all, siteId] as const,
};

export const useAreas = (siteId: SiteId) => {
  return useQuery({
    queryKey: areasKeys.bySite(siteId),
    queryFn: () => fetchAreas(siteId),
    staleTime: 1000 * 60 * 10,
  });
};

const fetchTeams = async (siteId: SiteId) => {
  return await patientApi.get(`/ambulating/${siteId}/teams`).json(teamsSchema);
};

const teamsKeys = {
  all: ["teams"] as const,
  bySite: (siteId: SiteId) => [...teamsKeys.all, siteId] as const,
};

export const useTeams = (siteId: SiteId) => {
  return useQuery({
    queryKey: teamsKeys.bySite(siteId),
    queryFn: () => fetchTeams(siteId),
    staleTime: 1000 * 60 * 10,
  });
};

const fetchWards = async (siteId: SiteId) => {
  return await patientApi.get(`/ambulating/${siteId}/wards`).json(wardsSchema);
};

const wardsKeys = {
  all: ["wards"] as const,
  bySite: (siteId: SiteId) => [...wardsKeys.all, siteId] as const,
};

export const useWards = (siteId: SiteId) => {
  return useQuery({
    queryKey: wardsKeys.bySite(siteId),
    queryFn: () => fetchWards(siteId),
    staleTime: 1000 * 60 * 10,
  });
};

export const normalizePatientIdentifier = ({
  identifier,
  identifierType,
}: {
  identifier: string;
  identifierType: IPersonalIdentityNumberType;
}) => {
  switch (identifierType) {
    case personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber:
      return identifier.replace("-", "");
    case personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
      return identifier;
    case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
      return identifier.replace("-", "");
    default:
      throw identifierType satisfies never;
  }
};

export const sparPersonSchema = z.object({
  givenName: z.string().nullish(),
  familyName: z.string().nullish(),
  address: z
    .object({
      addressLine1: z.string().nullish(),
      addressLine2: z.string().nullish(),
      postalCode: z.string().nullish(),
      city: z.string().nullish(),
    })
    .nullish(),
});

export type ISparPerson = z.infer<typeof sparPersonSchema>;

// SPAR is queried per site because sites can belong to different SPAR end customers.
export const sparPersonQueryOptions = ({
  siteId,
  pin,
}: {
  siteId: SiteId;
  pin: string;
}) =>
  queryOptions({
    queryKey: ["sparPerson", siteId, pin],
    queryFn: async () => {
      // SPAR returns a list of matches, but we only ever use the first entry.
      const [sparPerson] = await patientApi
        .get(`/ambulating/${siteId}/patients/spar`, { searchParams: { pin } })
        .json(z.array(sparPersonSchema));
      return sparPerson ?? null;
    },
  });

const careEpisodeAddressSchema = addressWithCoordinates.extend({
  postalCode: z.union([
    internalPostalCodeSchema.shape.CH,
    internalPostalCodeSchema.shape.LV,
    internalPostalCodeSchema.shape.SE,
  ]),
});

// The fields an organization can switch on for its patients, one value per field. Which of
// them a form asks for is the form's decision — the endpoints take any of them — so a
// payload declares the whole group and sends what it collected.
const careEpisodeInformationSchema = z.object({
  careSummary: z.string().nullish(),
  diagnosisCode: z.string().nullish(),
  externalCareEpisodeId: z.string().nullish(),
  flags: z.string().nullish(),
  healthcareJourneyNumber: z.string().length(8).nullish(),
  homeCare: z.boolean().nullish(),
  information: z.string().nullish(),
  language: z.string().nullish(),
  lifeCare: z.boolean().nullish(),
  paymentExempt: z.boolean().nullish(),
  pet: z.string().nullish(),
  placeOfAssessment: z.string().nullish(),
  plannedAdmissionDateTime: calendarDateSchema.nullish(),
  primaryResponsible: z.number().int().nullish(),
  postDischargeProcessed: z.boolean().nullish(),
  reasonForAdmission: z.string().nullish(),
  referringUnit: z.string().nullish(),
  safetyAlarmText: z.string().nullish(),
});

// What `POST /care-episodes` takes. The identity a registration settles, an address, and
// the care episode fields any write can carry.
const careEpisodeRegistrationSchema = z.object({
  careEpisodeId: z.uuid(),
  address: careEpisodeAddressSchema,
  name: givenAndFamilyNameSchema,
  patientType: patientTypeSchema,
  personalIdentityNumber: z.string(),
  personalIdentityNumberType: personalIdentityNumberTypeSchema,
  phoneNumber: rawPhoneNumberSchema.nullish(),
  siteId: siteSchema.shape.id,
  // Site-specific, and referenced by id where the read schemas carry the resolved object. A
  // site can have no areas and no teams; one that does not use wards or economic units is
  // served a single default instead, which is what these carry there. The endpoint requires
  // an economic unit.
  area: z.number().positive().nullish(),
  team: z.number().positive().nullish(),
  ward: z.number().positive().nullish(),
  economicUnit: z.number().positive(),
  ...careEpisodeInformationSchema.shape,
});

// The endpoint decodes its body as "returning patient, or else new patient", and
// `relatives` is the key that decides: the returning half requires it, so a registration
// for a patient nobody has met must carry no `relatives` key at all — sending one, even an
// empty array, is answered with `PatientNotFound`.
// A new care episode for a patient the organization has cared for before.
const newCareEpisodeSchema = careEpisodeRegistrationSchema.extend({
  relatives: z.array(relativeSchema),
});
export type INewCareEpisode = z.infer<typeof newCareEpisodeSchema>;

// A patient the organization has never met before, registered as a prospect awaiting
// assessment: the same body, with the `relatives` key kept out by the type rather than by
// whoever writes the next call site.
const newProspectCareEpisodeSchema = careEpisodeRegistrationSchema.extend({
  relatives: z.never().optional(),
});
export type INewProspectCareEpisode = z.infer<
  typeof newProspectCareEpisodeSchema
>;

export const registerNewProspectCareEpisode = async (
  careEpisode: INewProspectCareEpisode,
) => {
  await patientApi.post(`/ambulating/care-episodes`, {
    json: newProspectCareEpisodeSchema.encode(careEpisode),
  });
};

export const registerNewCareEpisode = async (careEpisode: INewCareEpisode) => {
  await patientApi.post(`/ambulating/care-episodes`, {
    json: newCareEpisodeSchema.encode(careEpisode),
  });
};

const _newCloseContactSchema = z.object({
  name: z.string(),
  phone: rawPhoneNumberSchema.nullish(),
  isLivingTogether: z.boolean(),
  relation: z.string(),
});

const admitPatientSchema = z.object({
  admissionDate: calendarDateSchema,
  address: careEpisodeAddressSchema,
  area: z.number().positive().nullish(),
  team: z.number().positive().nullish(),
  ward: z.number().positive().nullish(),
  ...careEpisodeInformationSchema.omit({ language: true }).shape,
  phone: rawPhoneNumberSchema,
  relative: _newCloseContactSchema.nullish(),
});
export type IAdmitPatient = z.infer<typeof admitPatientSchema>;

export const admitPatient = async ({
  patientId,
  admitPatientFormData,
}: {
  patientId: string;
  admitPatientFormData: IAdmitPatient;
}) => {
  await patientApi.post(`/ambulating/patients/${patientId}/admit`, {
    json: admitPatientSchema.encode(admitPatientFormData),
  });
};

export const declinePatient = async ({
  patientId,
  category,
  activitiesToClose,
}: {
  patientId: string;
  category: IDeclinedCategory;
  activitiesToClose: string[];
}) => {
  await patientApi.post(`/ambulating/patients/${patientId}/decline`, {
    json: { category, activitiesToClose },
  });
};

const dischargePatientSchema = z.object({
  /**
   * A date before today. Leave it out to discharge now: the backend compares
   * dates in UTC, so sending today's local date shortly after midnight would be
   * rejected as a date in the future.
   */
  dischargedAt: calendarDateSchema.optional(),
  activitiesToClose: z.array(z.string()),
});
export type IDischargePatient = z.infer<typeof dischargePatientSchema>;

export const dischargePatient = async ({
  patientId,
  discharge,
}: {
  patientId: string;
  discharge: IDischargePatient;
}) => {
  await patientApi.post(`/ambulating/patients/${patientId}/discharge`, {
    json: dischargePatientSchema.encode(discharge),
  });
};

export const patientIdentifierQueryParamKey = {
  SwedishPersonalIdentityNumber: "swedish-identity-number",
  SwedishTemporaryIdentityNumber: "temporary-number",
  SwissEMedicusNumber: "swiss-emedicus-number",
  SwissPatientNumber: "swiss-patient-number",
  LatvianPersonasKods: "latvian-personas-kods",
} as const satisfies Record<IPersonalIdentityNumberType, string>;

type IdentifierQueryParams = {
  limit?: number;
  sort?: "desc" | "asc";
  "swedish-identity-number"?: string;
  "temporary-number"?: string;
  "swiss-emedicus-number"?: string;
  "swiss-patient-number"?: string;
  "latvian-personas-kods"?: string;
};

export const fetchCareEpisodes = async ({
  queryParams,
}: {
  queryParams: IdentifierQueryParams;
}) => {
  const queryString = generateQueryString(queryParams);
  const parsedCareEpisodes = await patientApi
    .get(`/ambulating/care-episodes${queryString}`)
    .json(z.array(careEpisodeSchema));
  return parsedCareEpisodes;
};

export const patientLatestCareEpisodeQueryOptions = ({
  identifier,
  identifierType,
}: {
  identifier: string;
  identifierType: IPersonalIdentityNumberType;
}) => {
  const queryParams: IdentifierQueryParams = {
    limit: 1,
    sort: "desc",
    [patientIdentifierQueryParamKey[identifierType]]:
      normalizePatientIdentifier({ identifier, identifierType }),
  };

  return queryOptions({
    queryKey: ["patientLatestCareEpisode", identifier, queryParams],
    queryFn: async () => {
      const [latestCareEpisode] = await fetchCareEpisodes({ queryParams });
      if (!latestCareEpisode) {
        throw new Error(`No care episode found for ${identifier}.`);
      }
      return latestCareEpisode;
    },
  });
};
